import { compileEntityFromBase } from '../compile/from-base-entry';
import { entityRuntimeMetadata } from '../compile/entity-runtime-metadata';
import { inventoryRuntimeMetadata } from '../compile/inventory-runtime-metadata';
import { createClassificationContext } from '../compile/from-base-classification-context';
import { createCatalogDefaultProviderReader } from '../compile/provider-catalog-order';
import { readNodeItemId } from '../compile/layer-cell-readers';
import { readItemField } from '../compile/item-field-reader';
import { readInventoryField, readLayerListEntryUuid } from '../compile/inventory-raw-readers';
import { surfaceField } from '../compile/from-base-encounter-media';
import { fromBaseClone, fromBaseRecord, fromBaseUuid, FromBaseInputMissing } from '../compile/from-base-context';
import { ftFloorBandRoomInputs, ftFloorBandRoomFromInputs, ftShapeRoofDeckBoxes } from '../port/k2-geometry/ft3d_floor_band';
import { native } from '../port/k2-geometry/php-shapes';
import { SIZE_WIDTHS_FT } from '../inventory/lift-occupancy';
import { boxIsNavigationOpenGround } from '../../../../ft3d/engine/ft3d-shape-core.js';
import { circleOccupancy } from '../../../../ft3d/engine/ft3d-move-core.js';
import { snapBesideProp } from '../movement/shapeless-prop-snap';
import { drawnCreatureBody, drawnBodyVolume } from '../../shared/physical-creature-body';
import { posCoord } from '../../shared/array-types';
import { fabricateRows } from '../actions/ability-preconditions';
import { resolveEquipSlot } from '../compile/possessor-compile';
import { slotLimitVerdict, waistBeltVerdict, equipFreeHandVerdict, type EquipRuleReaders } from '../inventory/equip-rules';
import { createCreatureEquippedCountsCompiler } from '../compile/creature-equipped-counts';
import { readAbilityEntryId } from '../compile/inventory-ability-grant-helpers';
import type { CreatureSpawnRequest, Row, SpawnReaders, SpawnRowPlan } from './row-plan';
import { emptySpawnPlan, appendSpawnStatus, storedSpawnPoint } from './row-plan';
import contract from '../../../catalogs/janus/row-contract.json';
import { compileInventoryTreeFromBase, type FromBaseInventoryTree } from '../compile/from-base-inventory';
import { ftClockColsFromArr, type CampaignClockCols } from '../../shared/campaign-clock';
import { getStatusEffectDurationTimes } from '../ability/statuses/get-status-effect-duration-times';
import { phpJsonInt } from '../compile/php-json-values';
import { inventoryCreationSizingTemplate } from '../inventory/creation-sizing';
import { createInventoryTemplateComposer } from '../inventory/compose-size-template';
import { isContainer, stackTarget, keyringVerdict } from '../inventory/move-rules';
import { spawnDraftTemplateRows } from './draft-template';
import { mergeInventoryMetadata } from '../compile/inventory-shared-catalog';
import { createFormulaReadContext } from '../compile/formula-read-context';
import { inventoryBirthPicture, inventoryBirthVitality, inventoryDurationReaders, inventoryBirthDeadline, appendInventoryBirthSpoilage, inventoryBirthLockpick, type InventoryBirthOptions } from '../inventory/birth-inputs-20';
import { _ftCamField } from '../../shared/campaign-field';
import { statusItemId, statusRows } from '../statuses/expiry';
import { inventoryIdentitySpawnReaders } from '../compile/inventory-delivery-20';
import { planInventorySpawnPlacement, type InventorySpawnPlacement } from './inventory-placement';
import { compileClassificationEntryFromBase } from '../compile/from-base-classification-entry';
import { constructSpawnInitializationSource } from './initialization-source';

export interface SpawnSourcePackage { base: Row; metadata: Parameters<typeof entityRuntimeMetadata>[0]; shared: Row; creature_previews?: Record<string, Row>; planning_only?: true }

export const spawnCreatureSourceKey = (id: string, template: string | null): string => `${id.toUpperCase()}:${(template ?? '').toUpperCase()}`;
/** Only an explicit table bucket witnesses an empty SELECT. Unrelated flat rows do not. */
export function spawnTableRows(base: Row, id: string): Row[] | null {
    if (fromBaseRecord(base.random_table_rows) && Object.prototype.hasOwnProperty.call(base.random_table_rows, id))
        return Array.isArray(base.random_table_rows[id]) ? base.random_table_rows[id] as Row[] : null;
    const rows = Array.isArray(base.random_table_ranges)
        ? (base.random_table_ranges as Row[]).filter(row => readItemField(row, 'item_id') === id) : [];
    return rows.length ? rows : null;
}
type TableReaders = Pick<SpawnReaders, 'entries' | 'value'>;
/** The same possible first/last tables drive input acquisition and the name roll. */
function spawnNameTables(row: Row, read: TableReaders): { first: string | string[]; last: string } | null {
    if (Number(readItemField(row, 'homebrew') ?? 0) === 1) return null;
    const table = (key: string): string => {
        const ids = read.entries(row, key).map(readLayerListEntryUuid).filter(Boolean);
        return ids[ids.length - 1] ?? '';
    };
    const male = table('random_name_table_male'), female = table('random_name_table_female');
    const generic = table('random_name_table'), last = table('random_name_table_last_name');
    if (![male, female, generic, last].some(fromBaseUuid)) return null;
    if (generic && !male && !female) return { first: generic, last };
    const gender = readLayerListEntryUuid(read.value(row, 'gender'));
    const humanMale = 'F145078C-472C-6095-8799-923202A8BADC', humanFemale = '1919E41C-FB3B-C2FF-E43D-02DD252CF95F';
    const first = gender === '1214C099-5021-24B5-5D8D-608B832CE70A' ? [female || humanFemale]
        : gender === '0D629F19-23AC-18BE-B79C-73CB52703F66' ? [male || humanMale]
            : [male || humanMale, female || humanFemale];
    return { first, last };
}
/** Inspect only this add's possible table reads, including runtime-omitted private definitions. */
export function spawnCreatureTableInputsMissing(base: Row, source: Row, request: CreatureSpawnRequest, read: TableReaders): boolean {
    const catalog = fromBaseRecord(base.catalog) ? base.catalog : {};
    const missing = (id: string): boolean => fromBaseUuid(id)
        && (!fromBaseRecord(catalog[id]) || spawnTableRows(base, id) === null);
    const names = request.name ? null : spawnNameTables(source, read);
    if (names && [...Array.isArray(names.first) ? names.first : [names.first], names.last].some(missing)) return true;
    if (request.loot === false || request.controllerId || readItemField(source, 'item_type') === 'lair') return false;
    const seen = new Set<string>();
    const lootMissing = (id: string, depth = 0): boolean => {
        if (!fromBaseUuid(id) || seen.has(id) || depth >= 8) return false;
        seen.add(id);
        if (missing(id)) return true;
        return spawnTableRows(base, id)!.some(row => {
            const attached = readLayerListEntryUuid(readItemField(row, 'item_id_attached'));
            const type = readItemField(fromBaseRecord(catalog[attached]) ? catalog[attached] : null, 'item_type')
                ?? readItemField(row, 'attached_type');
            return type === 'random_loot_table' && lootMissing(attached, depth + 1);
        });
    };
    return lootMissing(readLayerListEntryUuid(read.value(source, 'attached_loot_table')));
}
// Packages are immutable definition inputs. Share only their deterministic source compiles;
// placement, clocks, dice and final newborn rows belong to each planning attempt.
const creatureSources = new WeakMap<SpawnSourcePackage, Map<string, Row>>();

/** Request-owned raw catalog + complete compiler. Drafts never enter the shared output cache. */
export function createSpawnSourceReaders(data: SpawnSourcePackage, encounter: Row, uuid: () => string, die: (sides: number) => number,
    currentClock?: () => CampaignClockCols | null): SpawnReaders {
    const inventoryMetadata = inventoryRuntimeMetadata(data.metadata);
    let creatures = creatureSources.get(data);
    if (!creatures) { creatures = new Map(Object.entries(data.creature_previews ?? {})); creatureSources.set(data, creatures); }
    let fullMetadata: ReturnType<typeof entityRuntimeMetadata> | undefined;
    const metadata = (): ReturnType<typeof entityRuntimeMetadata> => fullMetadata ??= entityRuntimeMetadata(data.metadata);
    // Definition witnesses and sizing sources from both SELECT packages form one closure.
    const base = Object.keys(data.shared).length ? mergeInventoryMetadata(data.shared, data.base) : { ...data.base };
    const catalog: Record<string, Row> = {};
    for (const [id, row] of Object.entries(fromBaseRecord(base.catalog) ? base.catalog : {})) {
        if (!fromBaseRecord(row)) throw new Error(`spawn:raw-definition-shape:${id}`);
        catalog[id] = row;
    }
    base.catalog = catalog;
    base.enchantment_templates = [...Array.isArray(base.enchantment_templates) ? base.enchantment_templates : []];
    base.enchantment_template_enchantments = [...Array.isArray(base.enchantment_template_enchantments) ? base.enchantment_template_enchantments : []];
    const context = createClassificationContext(base, inventoryMetadata);
    const providers = createCatalogDefaultProviderReader({ catalogRows: inventoryMetadata.layerCatalog,
        resolveCarrierRef: context.definition, nodeItemId: readNodeItemId });
    const missingDefinition = (id: string): Error => {
        const shells = fromBaseRecord(base.inventory_metadata) && fromBaseRecord(base.inventory_metadata.shellRows)
            ? base.inventory_metadata.shellRows : {};
        const shell = fromBaseRecord(shells[id.toUpperCase()]) ? shells[id.toUpperCase()] as Row : null;
        return new Error(`spawn:definition-unavailable:${id}:${readItemField(shell, 'item_name') ?? 'Unknown item'}`);
    };
    const source = (id: string): Row => {
        let row: Row | null = null;
        try { row = context.rawDefinition(id); }
        catch (error) { if (!(error instanceof FromBaseInputMissing) || error.dependency !== 'definition') throw error; }
        if (!row) throw missingDefinition(id);
        return row;
    };
    const complete = (entity: 'creature' | 'definition', input: Row): Row => {
        const result = compileEntityFromBase(entity, input, metadata());
        if (result.stagesMissing.length || !fromBaseRecord(result.computed)) throw new Error(`spawn:compile:${result.stagesMissing.join(',')}`);
        return result.computed;
    };
    const prepared = new Map<string, Row>();
    const item = (id: string): Row => {
        const key = id.toUpperCase(); let row = prepared.get(key);
        if (!row) {
            source(key);
            if (data.planning_only) {
                row = context.definition(key) ?? undefined;
                if (!row) throw missingDefinition(key);
            } else {
                const result = compileInventoryTreeFromBase({ ...base, entity_id: key, base_rev: 0, inventory: [], inventory_order: [], metadata: inventoryMetadata } as unknown as FromBaseInventoryTree,
                    catalog, [key]);
                const missing = /^(?:definition:|known-missing-definition:)([0-9a-f-]{36})$/i.exec(result.detail ?? '');
                if (missing) throw missingDefinition(missing[1]!);
                if (result.missingStages.length || !result.definitions?.[key]) throw new Error(`spawn:item-compile:${result.missingStages.join(',')}:${result.detail ?? key}`);
                row = result.definitions[key];
            }
            prepared.set(key, row);
        }
        // Planning borrows immutable definitions; newborn instance rows are authored separately.
        return data.planning_only ? row : fromBaseClone(row);
    };
    const instances = new Map<string, Row>();
    const newborns = new Map<string, Row>();
    const instance = (node: Row): Row => {
        const definition = item(String(readInventoryField(node, 'inventory_item_id')));
        const template = readInventoryField(node, 'enchantment_template_id');
        const sized = fromBaseUuid(template) ? context.intrinsicEnchantment({ ...definition,
            item_layer_types: { ...fromBaseRecord(definition.item_layer_types) ? definition.item_layer_types : {}, enchantment_template_applied: template } }) : definition;
        const row = { ...sized, inventory: node.inventory };
        instances.set(String(readInventoryField(node, 'inventory_id')).toUpperCase(), row);
        return row;
    };
    const dependencies = { catalogRows: inventoryMetadata.layerCatalog, abilityTypes: inventoryMetadata.abilityTypes,
        aggregationStrategy: (key: string) => inventoryMetadata.layerCatalog[key]?.aggregation_strategy ?? 'host_wins',
        resolveCarrierRef: context.definition, resolveInventoryInstance: (id: string): Row => {
            const row = instances.get(id.toUpperCase());
            if (!row) throw new FromBaseInputMissing('inventory', id);
            return row;
        } };
    const readers = createFormulaReadContext(dependencies);
    const value = (row: Row, name: string): unknown => readers.layerValue(row, name);
    const entries = (row: Row, name: string): unknown[] => Object.values(readers.layerEntries(row, name));
    const registry = (key: string, name: string): string => {
        const list = data.metadata[key];
        if (!Array.isArray(list)) throw new Error(`spawn:registry-unavailable:${key}`);
        const found = list.find(row => fromBaseRecord(row) && readItemField(row, 'variable_name') === name);
        const id = fromBaseRecord(found) ? readItemField(found, 'item_id') : null;
        if (!fromBaseUuid(id)) throw new Error(`spawn:registry-entry:${key}:${name}`);
        return id;
    };
    const tableRows = (id: string, loot: boolean): Row[] => {
        const rows = spawnTableRows(base, id);
        if (rows) return rows;
        // A SELECT-witnessed empty table may yield no loot; a missing input may not.
        if (loot) throw new Error('spawn:loot-table-no-range');
        return [];
    };
    const tableHit = (id: string, loot = false): Row | null => {
        const table = source(id), ranges = tableRows(id, loot);
        const roll = (dice: number, sides: number, mod: number): number => {
            if (!Number.isSafeInteger(dice) || !Number.isSafeInteger(sides) || dice < 0 || dice > 100 || sides < 1) throw new Error('spawn:table-dice');
            return Array.from({ length: dice }, () => die(sides)).reduce((a, b) => a + b, 0) + mod;
        };
        const maximum = Math.max(0, ...ranges.map(row => Number(readItemField(row, 'roll_range_end'))));
        if (maximum < 1) return null;
        let total = loot && value(table, 'random_table_roll_dice_1') != null ? roll(Number(value(table, 'random_table_roll_dice_1')),
            Number(value(table, 'random_table_roll_die_1') ?? 0), Number(value(table, 'random_table_roll_mod_1') ?? 0)) : die(maximum);
        if (loot && value(table, 'random_table_roll_dice_2') != null) total += roll(Number(value(table, 'random_table_roll_dice_2')),
            Number(value(table, 'random_table_roll_die_2') ?? 0), Number(value(table, 'random_table_roll_mod_2') ?? 0));
        return [...ranges].sort((a, b) => Number(readItemField(b, 'roll_range_end')) - Number(readItemField(a, 'roll_range_end')))
            .find(row => total >= Number(readItemField(row, 'roll_range_begin')) && total <= Number(readItemField(row, 'roll_range_end'))) ?? null;
    };
    const inventory = (definition: Row, holder: Row, qty: number, template: string, distinct = false, options: InventoryBirthOptions = {}, path: readonly string[] = [], sizingHolder: Row = holder): SpawnRowPlan => {
        const id = String(readItemField(definition, 'item_id')), plan = emptySpawnPlan();
        if (!Number.isSafeInteger(qty) || qty < 1) throw new Error('spawn:inventory-quantity');
        const expiry = inventoryBirthDeadline(definition, holder, fromBaseRecord(encounter.campaign_arr) ? encounter.campaign_arr : null,
            (node, key) => node ? value(node, key) : null, options, currentClock);
        const holderId = String(readInventoryField(holder, 'inventory_id')
            ?? surfaceField(fromBaseRecord(holder.encounter_creatures) ? holder.encounter_creatures : holder, 'encounter_creature_id')
            ?? surfaceField(fromBaseRecord(holder.encounters) ? holder.encounters : holder, 'encounter_id')
            ?? surfaceField(fromBaseRecord(holder.locations) ? holder.locations : holder, 'location_id') ?? '');

        if (path.includes(id) || path.length >= 8) throw new Error('spawn:prefab-cycle');
        const append = (part: SpawnRowPlan): void => {
            plan.writes.push(...part.writes); (plan.logs ??= []).push(...part.logs ?? []);
        };
        const nestedHolder = (inventoryId: string): Row => ({ inventory: { inventory_id: inventoryId } });
        const rules = { value: (row: Row | null, key: string) => row ? value(row, key) : null,
            itemType: (row: Row | null) => String(readItemField(row ?? null, 'item_type') ?? ''),
            inventoryField: (row: Row | null, key: string) => readInventoryField(row ?? null, key), statusItemIds: (row: Row) => statusRows(row, 'target').map(statusItemId) };
        const container = isContainer(definition, rules);
        // Each container is a separate identity with its own contents and expiry cohort.
        if ((container || readItemField(definition, 'item_type') === 'book') && qty > 1) {
            for (let count = 0; count < qty; count++) append(inventory(definition, holder, 1, template, true, options, path, sizingHolder));
            return plan;
        }
        const packed = entries(definition, 'equipment_packed');
        if (packed.length) {
            const working: Row = { ...holder, inventory: [...Array.isArray(holder.inventory) ? holder.inventory : []] };
            const parts = fabricateRows(packed).map(entry => ({ ...entry, definition: item(entry.uuid) }));
            const bag = parts.find(part => readItemField(part.definition, 'item_type') === 'container');
            for (let count = 0; count < qty; count++) {
                let contentsHolder = working;
                if (bag) {
                    const born = inventory(bag.definition, working, bag.qty, bag.template || template, true, options, [...path, id], sizingHolder);
                    append(born);
                    const parent = born.writes.find(write => write.table === 'inventory' && write.op === 'insert');
                    if (!parent) throw new Error('spawn:pack-container-not-created');
                    contentsHolder = nestedHolder(String(readInventoryField({ inventory: parent.row }, 'inventory_id')));
                }
                for (const part of parts) if (part !== bag) {
                    const child = inventory(part.definition, contentsHolder, part.qty, part.template || template, distinct, options, [...path, id], sizingHolder);
                    append(child);
                    if (contentsHolder === working) (working.inventory as Row[]).push(...child.writes
                        .filter(write => write.table === 'inventory' && write.op === 'insert').map(write => ({ inventory: write.row })));
                }
            }
            return plan;
        }
        const sized = inventoryCreationSizingTemplate(definition, sizingHolder, value, options.inventorySize);
        const composer = createInventoryTemplateComposer({ enchantment_templates: base.enchantment_templates as Row[],
            enchantment_template_enchantments: base.enchantment_template_enchantments as Row[] }, uuid, insert => {
                const table = insert.kind === 'template' ? 'enchantment_templates' : 'enchantment_template_enchantments';
                (base[table] as Row[]).push(insert.kind === 'member'
                    ? { ...insert.row, enchantment_template_enchantment_i: (base[table] as Row[]).length } : insert.row);
                plan.writes.push({ table, op: 'insert', row: insert.row });
            });
        template = composer.compose(template, sized);
        const vitality = inventoryBirthVitality(definition);
        const pic = inventoryBirthPicture(definition, die);
        const row: Row = Object.fromEntries(contract.inventory.columns.map(column => [column, contract.inventory.numbers.includes(column) ? 0 : null]));
        Object.assign(row, { inventory_id: uuid(), inventory_item_id: id, inventory_name: readItemField(definition, 'item_name'),
            inventory_deleted: 0, possess_id: holderId || null,
            item_qty: qty, enchantment_template_id: template || null, equipped: null, inventory_cost_gp: null,
            inventory_description: null, inventory_flavor_text: null, inventory_gm_notes: null,
            inventory_written_in_language: readLayerListEntryUuid(value(definition, 'written_in_language')) || null,
            ...vitality,
            inventory_inspection_dc: Number(value(definition, 'inspection_dc') ?? 10), lockpick_degree_start: null, lockpick_degree_end: null,
            inventory_size: options.inventorySize ?? null, weight_override: options.weightOverride ?? null,
            x_pos: options.position?.x ?? 0, y_pos: options.position?.y ?? 0, z_pos: options.position?.z ?? 0,
            inventory_presentation_pic_id: pic, inventory_thumbnail_pic_id: pic });
        instance({ inventory: row });
        const held = Array.isArray(holder.inventory) ? holder.inventory as Row[] : [];
        if (!distinct && !options.inventorySize && options.weightOverride === undefined && !options.spoilage && !expiry) {
            const candidates = held.filter(node => readInventoryField(node, 'possess_id') === row.possess_id);
            const stack = stackTarget({ ...definition, inventory: row }, candidates.map(node => ({ ...item(String(readInventoryField(node, 'inventory_item_id'))), ...node })),
                { isFloor: !!surfaceField(fromBaseRecord(holder.encounters) ? holder.encounters : null, 'encounter_id'),
                    x: options.position?.x ?? 0, y: options.position?.y ?? 0, z: options.position?.z ?? 0 }, 0, rules);
            if ('unknown' in stack) throw new Error(`spawn:stack:${stack.unknown}`);
            if (stack.target) {
                const previous = held.find(node => readInventoryField(node, 'inventory_id') === stack.target)!;
                const before = Number(readInventoryField(previous, 'item_qty') ?? 1), next = before + qty;
                const newborn = newborns.get(stack.target.toUpperCase());
                if (newborn) newborn.item_qty = next;
                else plan.writes.push({ table: 'inventory', op: 'update', row: { inventory_id: stack.target, item_qty: next },
                    before: { item_qty: before, possess_id: holderId, inventory_deleted: Number(readInventoryField(previous, 'inventory_deleted') ?? 0) } });
                (previous.inventory as Row).item_qty = next;
                return plan;
            }
        }
        Object.assign(row, inventoryBirthLockpick(definition, (node, key) => node ? value(node, key) : null, die));
        plan.writes.push({ table: 'inventory', op: 'insert', row });
        newborns.set(String(row.inventory_id).toUpperCase(), row);
        if (expiry) appendInventoryBirthSpoilage(plan, read, String(row.inventory_id), expiry);
        for (const child of fabricateRows(entries(definition, 'container_contents'))) {
            const childDefinition = item(child.uuid), gate = keyringVerdict(definition, childDefinition, rules);
            if ('ok' in gate && !gate.ok) throw new Error(gate.error);
            append(inventory(childDefinition, nestedHolder(String(row.inventory_id)), child.qty, child.template, false,
                options.spoilage ? { spoilage: options.spoilage } : {}, [...path, id]));
        }
        return plan;
    };
    const read: SpawnReaders = {
        value, entries, ownEntries: (row, name) => Object.values(context.ownReaders.layerEntries(row, name)),
        providers: row => [...providers.providers(row)] as Row[], item, inventory, uuid, die,
        creature(id, template, draft) {
            const key = spawnCreatureSourceKey(id, template);
            const previous = !draft && (creatures.get(key) ?? data.creature_previews?.[key]);
            if (previous) return previous;
            const raw = source(id), seed = fromBaseClone(raw);
            if (readItemField(seed, 'item_type') === 'creature_model' && (template || draft)) (seed.items as Row).item_type = 'creature';
            // The draft has a request-local template identity and never changes catalog definitions.
            const draftId = 'C0DEC0DE-C5A5-4000-ABCD-000000000000';
            const draftRows = draft ? spawnDraftTemplateRows(draft, draftId, new Set(Object.keys(inventoryMetadata.layerCatalog))) : [];
            if (draftRows.length) template = draftId;
            const input = { ...base, ...seed, entity_id: id, statuses: { target: [], caster: [] }, charges: [], inventory: [],
                ...(draftRows.length ? { monster_template_layers: [...Array.isArray(base.monster_template_layers) ? base.monster_template_layers : [], ...draftRows] } : {}),
                ...(template ? { encounter_creatures: { encounter_creature_monster_template_id: template, item_id: id, item_type: readItemField(seed, 'item_type') } } : {}) };
            let result: Row;
            if (data.planning_only) {
                // Birth planning reads authored/provider values, not skills, AC,
                // castable lists or a throwaway creature's completed tree.
                const classified = compileClassificationEntryFromBase('creature', input, inventoryMetadata);
                if (!classified.row || classified.diagnostics.length) throw new Error(`spawn:classification:${classified.diagnostics.join(',')}`);
                result = constructSpawnInitializationSource(input, metadata().player, inventoryMetadata, classified.row);
            } else result = readItemField(seed, 'item_type') === 'lair' ? item(id) : complete('creature', input);
            if (!draft) creatures.set(key, result);
            return result;
        },
        name(row) {
            const tables = spawnNameTables(row, read);
            if (!tables) return null;
            const first = Array.isArray(tables.first) ? tables.first[die(tables.first.length) - 1]! : tables.first;
            const parts = [first, tables.last].filter(fromBaseUuid).map(id => tableHit(id)).filter((row): row is Row => !!row)
                .map(row => String(readItemField(row, 'roll_result_effect') ?? '').replace(/[\u00a0\u200b-\u200d\ufeff]/g, ' ').replace(/\s+/g, ' ').trim())
                .map(part => part.charAt(0).toUpperCase() + part.slice(1)).filter(Boolean);
            return parts.length ? parts.join(' ') : null;
        },
        equip(holder, node, preferred) {
            const carried = Array.isArray(holder.inventory) ? holder.inventory as Row[] : [];
            const fatRows = carried.map(instance);
            const equipped = dependencies.resolveInventoryInstance(String(readInventoryField(node, 'inventory_id')));
            const player = metadata().player;
            const counted = createCreatureEquippedCountsCompiler({ ...player.equippedCounts, ...dependencies,
                equipmentSlots: player.equipmentSlots, resolveCarrierRef: item, queryFullItem: item,
            })({ ...holder, inventory: fatRows }) as Row;
            const slot = preferred || resolveEquipSlot(counted, String(readInventoryField(node, 'inventory_id')), { definition: item });
            if (!slot) return null;
            const worn = fatRows.filter(row => readInventoryField(row, 'equipped') === slot);
            const limit = slotLimitVerdict(slot, worn.length), belt = waistBeltVerdict(slot, equipped, worn,
                { inventoryField: (row, key) => readInventoryField(row ?? null, key) });
            const slots = player.equipmentSlots;
            const equipment = fromBaseRecord(counted.computed) && fromBaseRecord(counted.computed.creature) && fromBaseRecord(counted.computed.creature.equipped)
                ? counted.computed.creature.equipped : {};
            const rules: EquipRuleReaders = {
                value: (row, key) => row ? value(row, key) : null, entries: (row, key) => row ? entries(row, key) : [],
                inventoryField: (row, key) => readInventoryField(row ?? null, key), itemType: row => String(readItemField(row ?? null, 'item_type') ?? ''),
                sizeId: row => readLayerListEntryUuid(value(row, 'size')), body: row => Number(value(row, 'body') ?? 0),
                count: (_row, key) => Number(readItemField(equipment, key) ?? 0),
                holds: (row, id) => ['feat', 'passive_ability'].some(key => entries(row, key).some(entry => readAbilityEntryId(entry,
                    { itemField: readers.itemField, resolveCarrierRef: item }).toUpperCase() === id.toUpperCase())),
                provides: (row, key) => value(row, key) != null || entries(row, key).length > 0,
                itemPassives: row => entries(row, 'passive_ability').map(entry => readAbilityEntryId(entry,
                    { itemField: readers.itemField, resolveCarrierRef: item })),
            };
            const gate = equipFreeHandVerdict(counted, equipped, slot, slots,
                { main: fatRows.find(row => readInventoryField(row, 'equipped') === slots.main_hand) ?? null,
                    off: fatRows.find(row => readInventoryField(row, 'equipped') === slots.off_hand) ?? null }, rules);
            if ('unknown' in gate) throw new Error(`spawn:equip:${gate.unknown}`);
            return 'ok' in limit && limit.ok && 'ok' in belt && belt.ok && gate.ok ? slot : null;
        },
        loot(sourceRow, holder) {
            const table = readLayerListEntryUuid(value(sourceRow, 'attached_loot_table'));
            const plan = emptySpawnPlan(); if (!fromBaseUuid(table)) return plan;
            let tableId = table;
            for (let depth = 0; depth < 8; depth++) {
                const hit = tableHit(tableId, true); if (!hit) return plan;
                const attached = readLayerListEntryUuid(readItemField(hit, 'item_id_attached'));
                if (!fromBaseUuid(attached)) {
                    if (String(readItemField(hit, 'roll_result_effect') ?? '').trim().toLowerCase() === '(no loot)') return plan;
                    throw new Error('spawn:loot-empty-attachment');
                }
                if (['020E057B-D2D5-4B0A-98B5-2B949A74E078','59DBEBB1-115D-25E9-9719-D816EBCE9FB5',
                    '150CBF1C-BD2C-45F4-8344-588E5C8BEBC3','EF8A5474-2CCF-4858-8A5B-26173DD47409'].includes(attached.toUpperCase()))
                    throw new Error('spawn:loot-sentinel-authoring-required');
                if (['B7C21D4E-3F80-4A19-9E62-0C5A7716D3A2','D41E9AB6-5C27-4F03-B8D9-6E2740F19C55'].includes(attached.toUpperCase()))
                    throw new Error('spawn:loot-explosion-plan-required');
                const definition = item(attached), type = readItemField(definition, 'item_type');
                if (type === 'random_loot_table') { tableId = attached; continue; }
                if (String(readItemField(definition, 'item_name') ?? '').toLowerCase() === '(no loot)') return plan;
                const dice = phpJsonInt(readItemField(hit, 'roll_dice')), sides = phpJsonInt(readItemField(hit, 'roll_die'));
                const qty = dice > 0 && sides > 0 ? Math.max(1, Array.from({ length: dice }, () => die(sides)).reduce((a, b) => a + b, 0) + phpJsonInt(readItemField(hit, 'roll_mod'))) : 1;
                const addition = inventory(definition, holder, qty, '');
                const first = addition.writes.find(write => write.table === 'inventory' && write.op === 'insert');
                if (first && entries(definition, 'equipment_slot').length) {
                    const current = Array.isArray(holder.inventory) ? holder.inventory as Row[] : [];
                    const probe = { ...holder, inventory: [...current, { inventory: { ...first.row, item_qty: 1 } }] };
                    const slot = read.equip(probe, { inventory: { ...first.row, item_qty: 1 } });
                    if (slot) {
                        if (qty > 1) { first.row.item_qty = 1; addition.writes.push({ table: 'inventory', op: 'insert', row: { ...first.row, inventory_id: uuid(), item_qty: qty - 1 } }); }
                        first.row.equipped = slot;
                    }
                }
                return addition;
            }
            throw new Error('spawn:loot-table-depth');
        },
        ground(row, point, direct, roster, unpointed = false) {
            const rawShape = surfaceField(fromBaseRecord(encounter.encounters) ? encounter.encounters : encounter, 'encounter_shape');
            const shape = typeof rawShape === 'string' ? JSON.parse(rawShape) as Row : fromBaseRecord(rawShape) ? rawShape : null;
            const boxes = shape && Array.isArray(shape.boxes) ? shape.boxes : [];
            // A non-scene Add has no picked elevation. Prefer exposed authored
            // terrain above its default workplane, rather than the hollow plinth
            // below it. Summons, transforms and explicit clicks keep their band.
            const supportZ = unpointed ? Math.max(point.z, ...boxes.flatMap(raw => {
                if (!fromBaseRecord(raw) || !boxIsNavigationOpenGround(raw)) return [];
                const x = fromBaseRecord(raw.x) ? raw.x : {}, y = fromBaseRecord(raw.y) ? raw.y : {};
                const floor = Number(raw.floor);
                return Number.isFinite(floor) && point.x >= Number(x.min) && point.x <= Number(x.max)
                    && point.y >= Number(y.min) && point.y <= Number(y.max) ? [floor] : [];
            })) : point.z;
            const decks = shape ? native(ftShapeRoofDeckBoxes(shape, supportZ)) : [];
            const room = native(ftFloorBandRoomFromInputs(ftFloorBandRoomInputs([...boxes, ...Array.isArray(decks) ? decks : []]), point.x, point.y, supportZ));
            const floor = fromBaseRecord(room) ? Number(readItemField(room, 'floor') ?? 0) : 0;
            const width = SIZE_WIDTHS_FT[readLayerListEntryUuid(value(row, 'size'))] ?? 5;
            const height = Number(value(row, 'creature_physics_height') ?? width);
            const z = boxes.length ? floor : height / 2;
            const speed = readLayerListEntryUuid(value(row, 'default_preferred_speed_type')) || read.walkingSpeed;
            if (speed === registry('speedTypes', 'speed_hovering')) return { ...point, z: z + 2 };
            if (speed === registry('speedTypes', 'speed_flying')) return { ...point, z: Math.max(z, point.z) };
            if (!direct && !boxes.length) {
                const body = drawnCreatureBody(row);
                const obstacles = roster.flatMap(other => {
                    const saved = fromBaseRecord(other.encounter_creatures) ? other.encounter_creatures : other;
                    if (Number(surfaceField(saved, 'encounter_creature_deleted') ?? 0) === 1
                        || surfaceField(saved, 'item_type') === 'lair') return [];
                    // Earlier summons in this plan have saved rows but have not been compiled into the encounter yet.
                    const source = fromBaseRecord(other.item_layer_types) ? other : {
                        ...read.creature(String(surfaceField(saved, 'item_id')),
                            surfaceField(saved, 'encounter_creature_monster_template_id') as string | null), encounter_creatures: saved };
                    const otherBody = drawnCreatureBody(source);
                    const position = fromBaseRecord(other.position) || fromBaseRecord(other.pos_arr) ? other : saved;
                    const at = { x: posCoord(position, 'x_pos') ?? 0, y: posCoord(position, 'y_pos') ?? 0,
                        z: Math.max(0, (posCoord(position, 'z_pos') ?? 0) - otherBody.heightFt / 2) };
                    const volume = drawnBodyVolume(otherBody, at, null, Number(surfaceField(saved, 'encounter_creature_dead') ?? 0) === 1);
                    return volume.zMin >= body.heightFt || volume.zMax <= 0 ? []
                        : [{ ...circleOccupancy(at.x, at.y, otherBody.r, 0, 1), r: otherBody.r }];
                });
                // The shared shapeless search bounds travel to 40 ft and checks the saved 3-dp point for overlap.
                const placed = snapBesideProp({ props: [] }, { ...point, z: 0 }, body.r, obstacles);
                if (!placed) throw new Error('spawn:placement:no-unobstructed-position');
                return { x: placed.x, y: placed.y, z: body.heightFt / 2 };
            }
            return { ...point, z };
        },
        enemyTeam: registry('teamCatalog', 'enemy'), walkingSpeed: registry('speedTypes', 'speed_walking'),
        hiddenStatus: registry('statusCatalog', 'hidden'), summonTag: registry('statusCatalog', 'summon_tag'),
        get etherId() { return registry('classMechanics', 'ether'); },
        casterDeathStatusIds: Array.isArray(data.metadata.statusCatalog) ? data.metadata.statusCatalog.filter(row =>
            fromBaseRecord(row) && readItemField(row, 'ends_on_caster_death') === true).map(row => String(readItemField(row, 'item_id')).toUpperCase()) : [],
        clock() {
            const campaign = fromBaseRecord(encounter.campaign_arr) ? encounter.campaign_arr : null;
            const id = _ftCamField(campaign, 'campaign_id');
            if (!fromBaseUuid(id)) return null;
            const clock = currentClock?.() ?? ftClockColsFromArr(campaign);
            return { campaignId: id, starts: [clock.year, clock.month, clock.day, clock.hour, clock.minute, clock.second].join('-') };
        },
    };
    return read;
}

/** A preview is a real inventory compile over a request-local row, never a definition with a forged facet. */
export function previewInventoryFromSources(data: SpawnSourcePackage, itemId: string, inventory: Row): Row {
    const base = mergeInventoryMetadata(data.shared, data.base), metadata = inventoryRuntimeMetadata(data.metadata);
    const parent = readInventoryField({ inventory }, 'possess_id');
    const owner = fromBaseUuid(parent) ? parent : itemId;
    // The native initializer consumes SQL columns, including audit/position fields that
    // a catalog-only preview has never saved. Null keeps authored cost and weight defaults.
    const saved = { ...Object.fromEntries(contract.inventory.columns.map(column => [column, null])),
        inventory_created_by_id: null, x_pos: 0, y_pos: 0, z_pos: 0, ...inventory, possess_id: owner };
    const result = compileInventoryTreeFromBase({ ...base, entity_id: owner, base_rev: 0, metadata,
        possessor: { inventory: { inventory_id: owner } }, encounter_links: [],
        inventory: [{ inventory: saved, base_rev: 0, statuses: { target: [], caster: [] }, charges: [],
            inventory_key_links: [], inventory_lock_bindings: [], inventory_closure_states: [], inventory_encounter_creature_links: [] }],
        inventory_order: [saved] } as unknown as FromBaseInventoryTree,
    fromBaseRecord(base.catalog) ? base.catalog as Record<string, Row> : {});
    if (result.missingStages.length || !result.inventory?.[0])
        throw new Error(`spawn:preview:${result.missingStages.join(',')}:${result.detail ?? itemId}`);
    const row = result.inventory[0];
    return { ...row, inventory: { ...row.inventory as Row, possess_id: parent ?? '' }, catalog: result.definitions };
}

/** Ordinary object birth reuses the inventory factory, then adds the form's instance state and attachments. */
export function planEncounterInventorySpawn(data: SpawnSourcePackage, encounter: Row, destination: string, values: Row,
    random: { uuid(): string; die(sides: number): number }, placement?: InventorySpawnPlacement): SpawnRowPlan {
    const read = inventoryIdentitySpawnReaders(createSpawnSourceReaders(data, encounter, random.uuid, random.die),
        mergeInventoryMetadata(data.shared, data.base), inventoryRuntimeMetadata(data.metadata));
    const itemId = String(values.inventory_item_id ?? '').toUpperCase(), quantity = Number(values.item_qty);
    if (!fromBaseUuid(itemId) || !Number.isSafeInteger(quantity) || quantity < 1) throw new Error('spawn:object-input');
    const definition = read.item(itemId), template = String(values.enchantment_template_id ?? '');
    const type = readItemField(definition, 'item_type');
    if (quantity > 1 && (type === 'container' || values.inventory_lock || values.inventory_locked))
        throw new Error('Containers and locked objects require quantity 1.');
    const holder = { encounter_creatures: { encounter_creature_id: destination }, inventory: [] };
    const plan = read.inventory(definition, holder, quantity, template, true);
    const roots = plan.writes.filter(write => write.table === 'inventory'
        && readInventoryField({ inventory: write.row }, 'possess_id') === destination);
    const status = (target: string, caster: string, item: string): void => {
        const ends = getStatusEffectDurationTimes({ read: inventoryDurationReaders(encounter,
            (row, key) => row ? read.value(row, key) : null) },
        { item_layer_types: { duration_int: 1, duration_type: 'permanent' } }, definition);
        appendSpawnStatus(plan, read, target, caster, item, '', { ...ends, connection: random.uuid() });
    };
    const registry = (name: string): string => {
        const rows = data.metadata.statusCatalog;
        const row = Array.isArray(rows) ? rows.find(row => fromBaseRecord(row) && readItemField(row, 'variable_name') === name) : null;
        const id = fromBaseRecord(row) ? readItemField(row, 'item_id') : null;
        if (!fromBaseUuid(id)) throw new Error(`spawn:status-source:${name}`);
        return id;
    };
    const placed = planInventorySpawnPlacement(encounter, values, random.die, placement), at = placed.position;
    for (const root of roots) {
        root.table = 'encounter_inventory';
        const id = String(readInventoryField({ inventory: root.row }, 'inventory_id'));
        for (const key of ['inventory_name', 'inventory_description', 'inventory_flavor_text', 'inventory_gm_notes', 'transform_3d'])
            if (values[key] != null) root.row[key] = String(values[key]);
        for (const key of ['inventory_cost_gp', 'inventory_inspection_dc'])
            if (values[key] != null && values[key] !== '') root.row[key] = Number(values[key]);
        root.row.inventory_unidentified = Number(values.inventory_unidentified ?? 0);
        // The form's "Private: visible to the GM only" flag (getCheckboxValue: the box's value when ticked, else 0). The browser
        // decides what players see and PHP persists the planned value; it no longer stamps the host encounter's own privacy.
        const privateFlag = values.inventory_private;
        root.row.inventory_private = privateFlag != null && privateFlag !== false && String(privateFlag) !== '0' && String(privateFlag) !== '' ? 1 : 0;
        root.row.x_pos = at.x; root.row.y_pos = at.y; root.row.z_pos = at.z;
        if (placed.mounted !== undefined) root.row.transform_3d = JSON.stringify({ ...placement?.transform,
            schema: 'ft.inventory-transform-3d.v1', mounted: placed.mounted });
        if (values.inventory_hidden) status(id, id, registry('hidden'));
        if (fromBaseUuid(values.inventory_ability_light_id)) status(id, id, values.inventory_ability_light_id);
        const lockId = String(values.inventory_lock ?? '');
        if (fromBaseUuid(lockId)) {
            const lock = read.item(lockId);
            const addition = read.inventory(lock, holder, 1, '', true);
            const inserted = addition.writes.find(write => write.table === 'inventory');
            if (!inserted) throw new Error('spawn:lock-row-missing');
            inserted.table = 'encounter_inventory';
            const lockInventoryId = String(readInventoryField({ inventory: inserted.row }, 'inventory_id'));
            for (const axis of ['x_pos', 'y_pos', 'z_pos']) inserted.row[axis] = root.row[axis];
            inserted.row.inventory_private = root.row.inventory_private; // the lock placed with a GM-only object is GM-only too
            plan.writes.push(...addition.writes);
            const integrated = Number(read.value(lock, 'integrated_object') ?? 0) === 1;
            plan.writes.push({ table: 'inventory_lock_bindings', op: 'insert', row: {
                inventory_lock_binding_id: random.uuid(), inventory_lock_binding_lock_id: lockInventoryId,
                inventory_lock_binding_secured_id: id, inventory_lock_binding_installation: integrated ? 'integrated' : 'padlock' } });
            if (integrated) status(id, lockInventoryId, registry('integrated_object'));
            if (values.inventory_locked) status(id, lockInventoryId, registry('locked'));
            if (values.inventory_hidden) status(lockInventoryId, lockInventoryId, registry('hidden'));
        }
    }
    (plan.logs ??= []).push({ kind: 'add_inventory', name: String(values.inventory_name ?? readItemField(definition, 'item_name') ?? 'Object'), detail: `Added ${quantity}.` });
    return plan;
}
